import { supabase } from '../lib/supabase';
import type { AddressDetails } from './googlePlacesService';

export interface GuestBookingData {
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  createAccount?: boolean;
  password?: string;
  pickup: AddressDetails;
  dropoff: AddressDetails;
  stops?: AddressDetails[];
  pickupTime: string;
  dropoffTime?: string | null;
  vehicleFeatures: string[];
  supportWorkersCount: number;
  fareEstimate: number;
  bookingType: 'on_demand' | 'scheduled' | 'advance';
  leadTimeHours: number;
  timeMultiplier: number;
  bookingTypeDiscount: number;
  specialRequirements?: string | null;
  notes?: string | null;
  paymentMethod?: 'cash_bank' | 'stripe';
}

export interface GuestBookingResult {
  booking_id: string;
  access_token: string;
  guest_rider_id: string;
  success: boolean;
  message?: string;
  reference?: string;
  user_account?: {
    success: boolean;
    user_id?: string;
    account_status?: string;
    message?: string;
    action?: string;
    error?: string;
  };
}

function stopsForExtras(stops?: AddressDetails[]) {
  if (!stops?.length) return [];
  return stops.map((stop) => ({
    formattedAddress: stop.formattedAddress,
    postcode: stop.postcode ?? null,
    placeId: stop.placeId ?? null,
    latitude: stop.latitude,
    longitude: stop.longitude,
  }));
}

class GuestBookingService {
  /**
   * Supabase V1: single `bookings` row via Edge Function `booking-v1-create`.
   */
  async createGuestBooking(bookingData: GuestBookingData): Promise<GuestBookingResult> {
    const baseUrl =
      typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL
        ? String(import.meta.env.VITE_SUPABASE_URL).replace(/\/$/, '')
        : '';
    const anonKey =
      typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY
        ? String(import.meta.env.VITE_SUPABASE_ANON_KEY)
        : '';

    try {
      const payload = {
        customer_name: bookingData.guestName,
        customer_email: bookingData.guestEmail,
        customer_phone: bookingData.guestPhone,
        pickup_address: bookingData.pickup.formattedAddress,
        pickup_lat: bookingData.pickup.latitude,
        pickup_lng: bookingData.pickup.longitude,
        pickup_postcode: bookingData.pickup.postcode ?? null,
        pickup_place_id: bookingData.pickup.placeId ?? null,
        dropoff_address: bookingData.dropoff.formattedAddress,
        dropoff_lat: bookingData.dropoff.latitude,
        dropoff_lng: bookingData.dropoff.longitude,
        dropoff_postcode: bookingData.dropoff.postcode ?? null,
        dropoff_place_id: bookingData.dropoff.placeId ?? null,
        pickup_time: bookingData.pickupTime,
        fare_estimate: bookingData.fareEstimate,
        payment_method: bookingData.paymentMethod || 'cash_bank',
        extras: {
          stops: stopsForExtras(bookingData.stops),
          vehicle_features: bookingData.vehicleFeatures,
          support_workers_count: bookingData.supportWorkersCount,
          booking_type: bookingData.bookingType,
          lead_time_hours: bookingData.leadTimeHours,
          time_multiplier: bookingData.timeMultiplier,
          booking_type_discount: bookingData.bookingTypeDiscount,
          special_requirements: bookingData.specialRequirements ?? null,
          notes: bookingData.notes ?? null,
          dropoff_time: bookingData.dropoffTime ?? null,
          create_account_requested: Boolean(bookingData.createAccount),
        },
      };

      if (!baseUrl || !anonKey || baseUrl.includes('placeholder')) {
        return {
          booking_id: '',
          access_token: '',
          guest_rider_id: '',
          success: false,
          message: 'Booking service is misconfigured (missing Supabase URL or anon key)',
        };
      }

      const res = await fetch(`${baseUrl}/functions/v1/booking-v1-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify(payload),
      });

      const data = (await res.json()) as {
        success?: boolean;
        booking_id?: string;
        reference?: string;
        message?: string;
      };

      if (!res.ok) {
        console.error('❌ booking-v1-create HTTP', res.status, data);
        return {
          booking_id: '',
          access_token: '',
          guest_rider_id: '',
          success: false,
          message:
            data?.message || `Booking request failed (${res.status}). Please try again or contact support.`,
        };
      }

      if (data && data.success === true && data.booking_id) {
        return {
          booking_id: data.booking_id,
          reference: data.reference ?? '',
          access_token: '',
          guest_rider_id: '',
          success: true,
        };
      }

      return {
        booking_id: '',
        access_token: '',
        guest_rider_id: '',
        success: false,
        message: data?.message || 'Failed to create booking',
      };
    } catch (error) {
      console.error('❌ Guest booking service error:', error);
      let message = error instanceof Error ? error.message : 'An unexpected error occurred';
      const lower = message.toLowerCase();
      if (
        lower.includes('failed to fetch') ||
        lower.includes('load failed') ||
        lower.includes('networkerror')
      ) {
        message = 'Could not reach the booking server. Check your connection and try again.';
      }
      return {
        booking_id: '',
        access_token: '',
        guest_rider_id: '',
        success: false,
        message,
      };
    }
  }

  async getGuestBookingByToken(token: string): Promise<{ data: unknown | null; error: unknown }> {
    void token;
    return {
      data: null,
      error: { message: 'Guest token lookup removed in Supabase V1' },
    };
  }

  async getGuestBooking(bookingId: string): Promise<{ data: unknown | null; error: unknown }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('❌ Error fetching booking:', error);
      return { data: null, error };
    }
  }

  async updateGuestBookingStatus(
    bookingId: string,
    status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'
  ): Promise<{ data: unknown | null; error: unknown }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', bookingId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('❌ Error updating booking status:', error);
      return { data: null, error };
    }
  }

  async cancelGuestBooking(bookingId: string): Promise<{ data: unknown | null; error: unknown }> {
    return this.updateGuestBookingStatus(bookingId, 'cancelled');
  }

  async getAllGuestBookings(limit = 50): Promise<{ data: unknown[] | null; error: unknown }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      return { data: data || [], error };
    } catch (error) {
      console.error('❌ Error fetching all bookings:', error);
      return { data: null, error };
    }
  }

  async checkUserAccountStatus(email: string): Promise<{ data: unknown | null; error: unknown }> {
    void email;
    return { data: null, error: null };
  }

  async upgradeGuestToUserAccount(
    bookingId: string,
    password: string
  ): Promise<{ data: unknown | null; error: unknown }> {
    void bookingId;
    void password;
    return {
      data: null,
      error: { message: 'Not available in Supabase V1' },
    };
  }

  async mergeGuestBookingsToUser(userId: string): Promise<{ data: unknown | null; error: unknown }> {
    void userId;
    return {
      data: null,
      error: { message: 'Not available in Supabase V1' },
    };
  }
}

export const guestBookingService = new GuestBookingService();
